import React from 'react';
import { Constants } from '@pwc/pwc-deals-web-common';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { getLinkedAdjustmentsAsSourceForQofE } from '../../../../../common/engine/selectors/adjustmentLinkSelectors';
import {
  getAdjustmentTypesEbitdaContributionForQofE,
  getTableReadyAdjustmentSumsNotParticipatingInCalculationsForQofE,
  getTableReadyAdjustmentSumsParticipatingInCalculationsForQofE,
} from '../../../../../common/engine/selectors/adjustmentSelectors';
import { getAdjustmentTogglesForQofE } from '../../../../../common/engine/selectors/adjustmentTogglesSelectors';
import {
  getPercentOfRevenue,
  getPercentOfAdjustedRevenue,
  getAdjustedRevenue,
} from '../../../../../common/engine/selectors/aggregatorSelectors';
import {
  getReportedEbitda,
  getReportedRevenue,
} from '../../../../../common/engine/selectors/factCalculationsSelectors';
import { getTimePeriodsForQofE } from '../../../../../common/engine/selectors/timePeriodSelectors';
import {
  ADJUSTED_REVENUE_KEY,
  OTHER_CONSIDERATIONS_TOTALS_OVERRIDE_TEXT,
  REPORTED_EBITDA_KEY,
  REPORTED_REVENUE_KEY,
} from '../../../../../common/engine/utilities/constants';
import OverallTableHeader from '../../common/OverallTableHeader';
import ReportedFinancials from '../../common/ReportedFinancials';
import PercentageRow from './PercentageRow';
import AdjustmentGroupForMode from '../../common/AdjustmentGroup/AdjustmentGroupForMode';

const QofETable = ({
  isClientView,
  contentVersionNumber,
  contentId,
  projectId,
  forUseWithPublishingSSRService,
  nameOfAdjustmentToExpand,
  targetAdjustmentType,
  targetAdjustmentName,
  isEditMode,
  adjustmentOptions,
  targetDocumentOptions,
  selectedLinkedAdjustments,
  setFieldValue,
  showLinkedAdjustments,
}) => {
  const qofeFinancialPeriods = useSelector(getTimePeriodsForQofE);
  const reportedRevenue = useSelector(getReportedRevenue);
  const adjustedRev = useSelector(getAdjustedRevenue);
  const reportedEbitda = useSelector(getReportedEbitda);
  const adjustmentTypesEbitdaContribution = useSelector(
    getAdjustmentTypesEbitdaContributionForQofE
  );
  const percentOfRevenue = useSelector(getPercentOfRevenue);
  const percentOfAdjustedRevenue = useSelector(getPercentOfAdjustedRevenue);
  const participatingAdjustmentSums = useSelector(
    getTableReadyAdjustmentSumsParticipatingInCalculationsForQofE
  );
  const notParticipatingAdjustmentSums = useSelector(
    getTableReadyAdjustmentSumsNotParticipatingInCalculationsForQofE
  );
  const qofeAdjustmentToggles = useSelector(getAdjustmentTogglesForQofE);

  const links = useSelector(getLinkedAdjustmentsAsSourceForQofE);
  const linkedAdjustments = links.filter(
    (link) => !isClientView || !link.isBroken
  );
  const isPublishMode = forUseWithPublishingSSRService;

  return (
    <table
      role="table"
      className="analysis-table"
      data-testid="structured-analysis-view-QofE"
      aria-label="Quality of Earnings"
    >
      <thead>
        <OverallTableHeader
          timePeriods={qofeFinancialPeriods}
          isPublishMode={isPublishMode}
        />
      </thead>
      <tbody>
        <ReportedFinancials
          testId="reported-revenue"
          rowText={REPORTED_REVENUE_KEY}
          financials={reportedRevenue}
          timePeriods={qofeFinancialPeriods}
          isPublishMode={isPublishMode}
        />

        <ReportedFinancials
          testId="adjusted-revenue"
          rowText={ADJUSTED_REVENUE_KEY}
          financials={adjustedRev}
          timePeriods={qofeFinancialPeriods}
          isPublishMode={isPublishMode}
        />

        <ReportedFinancials
          testId="reported-ebitda"
          rowText={REPORTED_EBITDA_KEY}
          financials={reportedEbitda}
          timePeriods={qofeFinancialPeriods}
          className="reported-ebitda"
          isPublishMode={isPublishMode}
        />

        <PercentageRow
          rowText="Reported Revenue"
          financials={percentOfRevenue}
          timePeriods={qofeFinancialPeriods}
          isPublishMode={isPublishMode}
        />

        {participatingAdjustmentSums.map((adjustmentTypeGroup) => (
          <AdjustmentGroupForMode
            projectId={projectId}
            documentCode={Constants.QUALITY_OF_EARNINGS}
            contentId={contentId}
            contentVersionNumber={contentVersionNumber}
            key={adjustmentTypeGroup.adjustmentType}
            group={adjustmentTypeGroup.adjustments}
            groupName={adjustmentTypeGroup.adjustmentType}
            timePeriods={qofeFinancialPeriods}
            ebitdaContributions={adjustmentTypesEbitdaContribution}
            linkedAdjustments={linkedAdjustments}
            adjustmentToggles={qofeAdjustmentToggles}
            isLastGroup={adjustmentTypeGroup.isLastGroup}
            targetAdjustmentType={targetAdjustmentType}
            targetAdjustmentName={targetAdjustmentName}
            nameOfAdjustmentToExpand={nameOfAdjustmentToExpand}
            isPublishMode={isPublishMode}
            isEditMode={isEditMode}
            adjustmentOptions={adjustmentOptions}
            targetDocumentOptions={targetDocumentOptions}
            selectedLinkedAdjustments={selectedLinkedAdjustments}
            setFieldValue={setFieldValue}
            showLinkedAdjustments={showLinkedAdjustments}
            isClientView={isClientView}
          />
        ))}

        <PercentageRow
          rowText="Adjusted Revenue"
          financials={percentOfAdjustedRevenue}
          timePeriods={qofeFinancialPeriods}
          isPublishMode={isPublishMode}
        />

        {notParticipatingAdjustmentSums.map((adjustmentTypeGroup) => (
          <AdjustmentGroupForMode
            projectId={projectId}
            documentCode={Constants.QUALITY_OF_EARNINGS}
            contentId={contentId}
            contentVersionNumber={contentVersionNumber}
            key={adjustmentTypeGroup.adjustmentType}
            group={adjustmentTypeGroup.adjustments}
            groupName={adjustmentTypeGroup.adjustmentType}
            timePeriods={qofeFinancialPeriods}
            linkedAdjustments={linkedAdjustments}
            totalOverrideValue={OTHER_CONSIDERATIONS_TOTALS_OVERRIDE_TEXT}
            showToggles={false}
            showTotalRow={false}
            targetAdjustmentType={targetAdjustmentType}
            targetAdjustmentName={targetAdjustmentName}
            nameOfAdjustmentToExpand={nameOfAdjustmentToExpand}
            isPublishMode={isPublishMode}
            isEditMode={isEditMode}
            adjustmentOptions={adjustmentOptions}
            targetDocumentOptions={targetDocumentOptions}
            selectedLinkedAdjustments={selectedLinkedAdjustments}
            setFieldValue={setFieldValue}
            showLinkedAdjustments={showLinkedAdjustments}
            isClientView={isClientView}
          />
        ))}
      </tbody>
    </table>
  );
};

QofETable.propTypes = {
  isClientView: PropTypes.bool,
  contentId: PropTypes.number.isRequired,
  contentVersionNumber: PropTypes.number.isRequired,
  projectId: PropTypes.string.isRequired,
  targetAdjustmentType: PropTypes.string,
  targetAdjustmentName: PropTypes.string,
  nameOfAdjustmentToExpand: PropTypes.string,
  forUseWithPublishingSSRService: PropTypes.bool,
};

QofETable.defaultProps = {
  isClientView: false,
  targetAdjustmentType: null,
  targetAdjustmentName: null,
  nameOfAdjustmentToExpand: null,
  forUseWithPublishingSSRService: false,
};

export default QofETable;
